import React, { useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import {
    WxbButton,
    WxbDataTable,
    WxbDrawer,
    WxbSegmented,
    WxbTag,
} from '../../wxb-ui';
import {
    buildShiftWindow,
    resolveOperationExecutionWindow,
    V4Operation,
    V4PersistedAssignment,
    V4ShiftAssignment,
} from '../resultDomain';
import AssignmentCalendarView from '../views/AssignmentCalendarView';
import {
    deriveMatrixOperationAssignments,
    MatrixOperationAssignment,
} from './scheduleMatrixOperations';
import '../SolverV4.css';

dayjs.locale('zh-cn');

type MatrixMode = 'shift' | 'operation';

interface ScheduleMatrixProps {
    shiftAssignments: V4ShiftAssignment[];
    assignments: V4PersistedAssignment[];
    calendarDays: { date: string; is_workday: boolean }[];
    operations?: V4Operation[];
    onEditShift: (shiftAssignment: V4ShiftAssignment) => void;
}

interface MatrixDate {
    date: string;
    isWorkday: boolean;
    dayOfWeek: number;
}

interface EmployeeRowData {
    id: number;
    name: string;
    code: string;
    shifts: Record<string, V4ShiftAssignment>;
    operations: Map<string, MatrixOperationAssignment[]>;
    totalHours: number;
    nightCount: number;
    weekendCount: number;
    hasIssue: boolean;
}

interface DetailTarget {
    employee: EmployeeRowData;
    date: string;
}

const isWorkShift = (shift?: V4ShiftAssignment): boolean => {
    if (!shift) return false;
    const attendanceKind = String(shift.attendance_kind || shift.plan_type || '').toUpperCase();
    return attendanceKind ? attendanceKind === 'WORK' : Number(shift.nominal_hours || 0) > 0.01;
};

const isLeaveShift = (shift?: V4ShiftAssignment): boolean => {
    if (!shift) return false;
    const attendanceKind = String(shift.attendance_kind || shift.plan_type || '').toUpperCase();
    return attendanceKind === 'PAID_LEAVE' || attendanceKind === 'UNPAID_LEAVE';
};

const isOperationCovered = (
    operation: MatrixOperationAssignment,
    shift?: V4ShiftAssignment,
): boolean => {
    if (!shift || !isWorkShift(shift)) return false;
    if (operation.shift_id != null && operation.shift_id !== shift.shift_id) return false;
    const start = dayjs(operation.planned_start);
    const end = dayjs(operation.planned_end);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return false;
    const window = buildShiftWindow(shift.date, shift);
    return !start.isBefore(window.start) && !end.isAfter(window.end);
};

const shiftTone = (shift: V4ShiftAssignment, hasOperations: boolean): string => {
    if (isLeaveShift(shift)) return 'leave';
    if (!isWorkShift(shift)) return 'rest';
    if (!hasOperations) return 'idle';
    if (shift.is_night_shift) return 'night';
    if (String(shift.shift_name || '').includes('长白')) return 'long-day';
    return 'day';
};

const shiftLabel = (shift: V4ShiftAssignment): string => {
    if (isLeaveShift(shift)) return '假';
    if (!isWorkShift(shift)) return '休';
    return String(Number(shift.credited_hours ?? shift.nominal_hours ?? 0));
};

const ScheduleMatrix: React.FC<ScheduleMatrixProps> = ({
    shiftAssignments,
    assignments,
    calendarDays,
    operations,
    onEditShift,
}) => {
    const [mode, setMode] = useState<MatrixMode>('shift');
    const [idleOnly, setIdleOnly] = useState(false);
    const [detailTarget, setDetailTarget] = useState<DetailTarget | null>(null);
    const [operationTarget, setOperationTarget] = useState<V4Operation | null>(null);

    const dates = useMemo<MatrixDate[]>(() => (
        (calendarDays || [])
            .map((day) => ({
                date: String(day.date).slice(0, 10),
                isWorkday: Boolean(day.is_workday),
                dayOfWeek: dayjs(day.date).day(),
            }))
            .sort((left, right) => left.date.localeCompare(right.date))
    ), [calendarDays]);
    const today = dayjs().format('YYYY-MM-DD');

    const currentOperationAssignments = useMemo(() => (
        deriveMatrixOperationAssignments(operations, shiftAssignments, assignments)
    ), [assignments, operations, shiftAssignments]);

    const employeeData = useMemo<EmployeeRowData[]>(() => {
        const employeeMap = new Map<number, EmployeeRowData>();
        const getEmployee = (
            employeeId: number,
            name = '',
            code = '',
        ): EmployeeRowData => {
            if (!employeeMap.has(employeeId)) {
                employeeMap.set(employeeId, {
                    id: employeeId,
                    name: name || `员工${employeeId}`,
                    code,
                    shifts: {},
                    operations: new Map(),
                    totalHours: 0,
                    nightCount: 0,
                    weekendCount: 0,
                    hasIssue: false,
                });
            }
            const employee = employeeMap.get(employeeId)!;
            if (name) employee.name = name;
            if (code) employee.code = code;
            return employee;
        };

        shiftAssignments.forEach((shift) => {
            const employee = getEmployee(
                shift.employee_id,
                shift.employee_name,
                shift.employee_code,
            );
            employee.shifts[shift.date] = shift;
            if (isWorkShift(shift)) {
                employee.totalHours += Number(shift.credited_hours ?? shift.nominal_hours ?? 0);
                if (shift.is_night_shift) employee.nightCount += 1;
                const date = dates.find((item) => item.date === shift.date);
                if (date && !date.isWorkday) employee.weekendCount += 1;
            }
        });

        currentOperationAssignments.forEach((assignment) => {
            const employee = getEmployee(
                assignment.employee_id,
                assignment.employee_name,
                assignment.employee_code,
            );
            const list = employee.operations.get(assignment.date) || [];
            list.push(assignment);
            employee.operations.set(assignment.date, list);
        });

        const employees = Array.from(employeeMap.values());
        const averageWeekend = employees.length
            ? employees.reduce((sum, employee) => sum + employee.weekendCount, 0) / employees.length
            : 0;
        employees.forEach((employee) => {
            const hasOrphan = Array.from(employee.operations.entries()).some(([date, items]) => (
                items.some((item) => !isOperationCovered(item, employee.shifts[date]))
            ));
            const weekendOutlier = employee.weekendCount > 2
                && employee.weekendCount > averageWeekend * 1.5;
            employee.hasIssue = hasOrphan || weekendOutlier;
        });
        return employees.sort((left, right) => left.name.localeCompare(right.name, 'zh-CN'));
    }, [currentOperationAssignments, dates, shiftAssignments]);

    const columns = useMemo<ColumnsType<EmployeeRowData>>(() => {
        const employeeColumn: ColumnsType<EmployeeRowData>[number] = {
            key: 'employee',
            title: '员工',
            fixed: 'left',
            width: 150,
            render: (_, employee) => (
                <div className={`schedule-matrix-employee ${employee.hasIssue ? 'has-issue' : ''}`}>
                    <span className="schedule-matrix-employee-name">{employee.name}</span>
                    <span className="schedule-matrix-employee-code">{employee.code}</span>
                </div>
            ),
        };
        const dateColumns: ColumnsType<EmployeeRowData> = dates.map((date) => ({
            key: date.date,
            width: 42,
            align: 'center',
            title: (
                <div className={[
                    'schedule-matrix-date-header',
                    date.date === today ? 'today' : '',
                    !date.isWorkday ? 'non-workday' : '',
                ].filter(Boolean).join(' ')}>
                    <span>{dayjs(date.date).format('D')}</span>
                    <small>{['日', '一', '二', '三', '四', '五', '六'][date.dayOfWeek]}</small>
                </div>
            ),
            render: (_, employee) => {
                const shift = employee.shifts[date.date];
                const operationItems = employee.operations.get(date.date) || [];
                const hasOperations = operationItems.length > 0;
                const hasOrphan = operationItems.some((item) => !isOperationCovered(item, shift));
                const idle = isWorkShift(shift) && !hasOperations;
                const operationLabel = operationItems.length > 2
                    ? `${operationItems[0].operation_name.slice(0, 3)}+${operationItems.length - 1}`
                    : operationItems.map((item) => item.operation_name.slice(0, 3)).join('/');
                return (
                    <WxbButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={[
                            'schedule-matrix-cell',
                            !date.isWorkday ? 'non-workday' : '',
                            hasOrphan ? 'orphan' : '',
                            idleOnly && !idle ? 'dimmed' : '',
                            idleOnly && idle ? 'spotlight' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setDetailTarget({ employee, date: date.date })}
                        aria-label={`${employee.name} ${date.date} 排班详情`}
                    >
                        {mode === 'shift' && shift && (
                            <span className={`schedule-matrix-shift tone-${shiftTone(shift, hasOperations)}`}>
                                {shiftLabel(shift)}
                            </span>
                        )}
                        {mode === 'operation' && hasOperations && (
                            <span className="schedule-matrix-operation">{operationLabel}</span>
                        )}
                        {operationItems.length > 1 && (
                            <span className="schedule-matrix-operation-count">{operationItems.length}</span>
                        )}
                        {hasOrphan && <span className="schedule-matrix-orphan-badge">异常</span>}
                    </WxbButton>
                );
            },
        }));
        const statsColumn: ColumnsType<EmployeeRowData>[number] = {
            key: 'stats',
            title: '统计',
            fixed: 'right',
            width: 100,
            render: (_, employee) => (
                <span className="schedule-matrix-stats">
                    {Number(employee.totalHours.toFixed(1))}h / {employee.nightCount}夜
                </span>
            ),
        };
        return [employeeColumn, ...dateColumns, statsColumn];
    }, [dates, idleOnly, mode, today]);

    const detailShift = detailTarget?.employee.shifts[detailTarget.date];
    const detailOperations = detailTarget?.employee.operations.get(detailTarget.date) || [];
    const detailHasOrphan = detailOperations.some((item) => !isOperationCovered(item, detailShift));
    const operationTargetExecution = operationTarget
        ? resolveOperationExecutionWindow(operationTarget)
        : null;
    const operationTargetPositions = operationTarget?.positions || [];
    const operationTargetAssignedCount = operationTargetPositions.filter((position) => (
        position.status === 'ASSIGNED' && position.employee
    )).length;
    const operationStatus = operationTarget?.status === 'COMPLETE'
        ? { label: '已覆盖', color: 'green' as const }
        : operationTarget?.status === 'PARTIAL'
            ? { label: '部分覆盖', color: 'amber' as const }
            : { label: '未覆盖', color: 'red' as const };

    return (
        <div className="schedule-matrix-container">
            <div className="schedule-matrix-toolbar">
                <WxbSegmented
                    size="sm"
                    value={mode}
                    onChange={(value) => setMode(value as MatrixMode)}
                    options={[
                        { value: 'shift', label: '班次', icon: <CalendarOutlined /> },
                        { value: 'operation', label: '操作', icon: <UnorderedListOutlined /> },
                    ]}
                />
                {mode === 'shift' ? (
                    <div className="schedule-matrix-legend">
                        <span><i className="schedule-matrix-legend-swatch day" />有操作</span>
                        <span><i className="schedule-matrix-legend-swatch idle" />在岗无操作</span>
                        <span><i className="schedule-matrix-legend-swatch leave" />请假</span>
                        <span><i className="schedule-matrix-legend-swatch rest" />休息</span>
                        <WxbButton
                            variant={idleOnly ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setIdleOnly((current) => !current)}
                        >
                            只看空岗
                        </WxbButton>
                    </div>
                ) : (
                    <div className="schedule-matrix-operation-summary">
                        按日期查看全部操作；点击操作可查看时间、批次和岗位分配
                    </div>
                )}
            </div>

            {mode === 'shift' ? (
                <WxbDataTable<EmployeeRowData>
                    className="schedule-matrix-table"
                    density="compact"
                    containered={false}
                    rowKey="id"
                    columns={columns}
                    dataSource={employeeData}
                    pagination={false}
                    scroll={{ x: 250 + dates.length * 42 }}
                    emptyState={{ description: '暂无排班结果' }}
                />
            ) : (
                <div className="schedule-matrix-operation-calendar">
                    <AssignmentCalendarView
                        operations={operations || []}
                        calendarDays={calendarDays}
                        selectedOpId={operationTarget?.operation_plan_id || null}
                        onSelectOperation={(operationId) => {
                            setOperationTarget(
                                operations?.find((operation) => (
                                    operation.operation_plan_id === operationId
                                )) || null,
                            );
                        }}
                    />
                </div>
            )}

            <WxbDrawer
                className="schedule-matrix-detail-drawer"
                title="排班明细"
                open={!!detailTarget}
                width={400}
                onClose={() => setDetailTarget(null)}
                footer={detailShift ? (
                    <div className="schedule-matrix-detail-actions">
                        <WxbButton
                            variant="primary"
                            disabled={Boolean(detailShift.is_locked)}
                            onClick={() => {
                                onEditShift(detailShift);
                                setDetailTarget(null);
                            }}
                        >
                            {detailShift.is_locked ? '班次已锁定' : '编辑班次'}
                        </WxbButton>
                    </div>
                ) : null}
            >
                {detailTarget && (
                    <div className="schedule-matrix-detail">
                        <div className="schedule-matrix-detail-heading">
                            <strong>{detailTarget.employee.name}</strong>
                            <span>{detailTarget.employee.code}</span>
                        </div>
                        <div className="schedule-matrix-detail-date">
                            {dayjs(detailTarget.date).format('YYYY-MM-DD dddd')}
                        </div>
                        {detailShift ? (
                            <div className="schedule-matrix-detail-shift">
                                <WxbTag color={isWorkShift(detailShift) ? 'blue' : isLeaveShift(detailShift) ? 'amber' : 'neutral'}>
                                    {detailShift.shift_name}（{detailShift.shift_code}）
                                </WxbTag>
                                <span>
                                    {detailShift.start_time}–{detailShift.end_time}
                                    {' · '}{detailShift.credited_hours}h 计薪
                                </span>
                                {detailShift.is_locked && <WxbTag color="amber">已锁定</WxbTag>}
                            </div>
                        ) : (
                            <div className="schedule-matrix-detail-empty">休息 / 无排班</div>
                        )}
                        <div className="schedule-matrix-detail-section">
                            <h4>操作分配（{detailOperations.length}）</h4>
                            {detailOperations.length > 0 ? detailOperations.map((operation, index) => (
                                <div
                                    key={`${operation.operation_name}-${index}`}
                                    className={`schedule-matrix-detail-operation ${
                                        isOperationCovered(operation, detailShift) ? '' : 'orphan'
                                    }`}
                                >
                                    <strong>{operation.operation_name}</strong>
                                    <span>
                                        {dayjs(operation.planned_start).format('MM-DD HH:mm')}
                                        {' – '}
                                        {dayjs(operation.planned_end).format('MM-DD HH:mm')}
                                    </span>
                                </div>
                            )) : (
                                <div className="schedule-matrix-detail-empty">
                                    {isWorkShift(detailShift) ? '在岗无操作分配（空岗）' : '无操作分配'}
                                </div>
                            )}
                        </div>
                        {detailHasOrphan && (
                            <div className="schedule-matrix-detail-error">
                                存在未被当前工作班次完整覆盖的操作，请先调整班次或人员分配。
                            </div>
                        )}
                    </div>
                )}
            </WxbDrawer>

            <WxbDrawer
                className="schedule-matrix-operation-drawer"
                title="操作详情"
                open={Boolean(operationTarget)}
                width={480}
                onClose={() => setOperationTarget(null)}
            >
                {operationTarget && operationTargetExecution && (
                    <div className="schedule-matrix-operation-detail">
                        <div className="schedule-matrix-operation-detail-heading">
                            <div>
                                <strong>{operationTarget.operation_name}</strong>
                                <span>
                                    {operationTarget.batch_code === 'STANDALONE'
                                        ? '独立任务'
                                        : operationTarget.batch_code}
                                </span>
                            </div>
                            <WxbTag color={operationStatus.color}>{operationStatus.label}</WxbTag>
                        </div>

                        <div className="schedule-matrix-operation-detail-meta">
                            <span>
                                <ClockCircleOutlined />
                                {dayjs(operationTargetExecution.start).format('YYYY-MM-DD HH:mm')}
                                {' – '}
                                {dayjs(operationTargetExecution.end).format('YYYY-MM-DD HH:mm')}
                            </span>
                            <span>
                                <TeamOutlined />
                                岗位覆盖 {operationTargetAssignedCount}/{operationTargetPositions.length}
                            </span>
                        </div>

                        <div className="schedule-matrix-operation-position-list">
                            <h4>岗位分配</h4>
                            {operationTargetPositions.map((position) => {
                                const roster = position.employee
                                    ? shiftAssignments.find((shift) => (
                                        shift.employee_id === position.employee!.id
                                        && shift.date === position.roster_date
                                        && shift.shift_id === position.shift_id
                                    ))
                                    : null;
                                return (
                                    <div
                                        key={position.position_number}
                                        className={[
                                            'schedule-matrix-operation-position',
                                            position.status === 'ASSIGNED' ? 'assigned' : 'vacant',
                                        ].join(' ')}
                                    >
                                        <span className="schedule-matrix-operation-position-number">
                                            岗位 {position.position_number}
                                        </span>
                                        {position.employee ? (
                                            <div className="schedule-matrix-operation-position-person">
                                                <strong><UserOutlined /> {position.employee.name}</strong>
                                                <span>
                                                    {position.employee.code}
                                                    {roster ? ` · ${roster.shift_name}` : ''}
                                                    {position.roster_date ? ` · ${position.roster_date}` : ''}
                                                </span>
                                            </div>
                                        ) : (
                                            <WxbTag color="red">待分配</WxbTag>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </WxbDrawer>
        </div>
    );
};

export default React.memo(ScheduleMatrix);
